import React from 'react';
import { Box, IconButton, Typography, Tooltip } from '@mui/material';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import {
  ChartComponent,
  SeriesCollectionDirective,
  SeriesDirective,
  Inject,
  LineSeries,
  ColumnSeries,
  DateTime,
  Tooltip as ChartTooltip,
} from '@syncfusion/ej2-react-charts';
import { parseCalendarYmd } from '../../../../utils/calendarDate';

const Card = ({ children, sx = {} }) => (
  <Box
    sx={{
      bgcolor: 'background.paper',
      border: '0.5px solid',
      borderColor: 'divider',
      borderRadius: 2,
      p: 2,
      ...sx,
    }}
  >
    {children}
  </Box>
);

const SecLabel = ({ children, sx = {} }) => (
  <Typography
    sx={{
      fontSize: '10px',
      fontWeight: 500,
      textTransform: 'uppercase',
      letterSpacing: '0.06em',
      color: 'text.secondary',
      mb: 1.5,
      ...sx,
    }}
  >
    {children}
  </Typography>
);

const MS_PER_WEEK = 7 * 24 * 60 * 60 * 1000;
const WEEK_INTERVAL_STEPS = [1, 2, 4, 8, 12, 26];

/** Pick a week interval that keeps ~12–16 x-axis labels without affecting tooltips. */
const getXAxisWeekInterval = (planData, actualData) => {
  const times = [...(planData || []), ...(actualData || [])]
    .map((d) => new Date(d.x).getTime())
    .filter((t) => !isNaN(t));
  if (times.length < 2) return 2;

  const spanWeeks = (Math.max(...times) - Math.min(...times)) / MS_PER_WEEK;
  const target = Math.ceil(spanWeeks / 14);
  return WEEK_INTERVAL_STEPS.find((step) => step >= target) ?? 26;
};

/**
 * Reusable headcount chart — Syncfusion ColumnSeries (actual FTE) + LineSeries (planned FTE).
 *
 * Props:
 *   title       — chart heading
 *   subtitle    — muted caption under the title (work-package scope)
 *   fillColor   — column bar fill (e.g. '#1D9E75' shop, '#378ADD' field)
 *   borderColor — column bar border
 *   actualData  — [{ x: Date, y: number|null, workers?: number, overtimeMultiplier?: number, avgHours?: number }]
 *   planData    — [{ x: Date, y: number }]        line runs full project duration
 */
const DEFAULT_SUBTITLE =
  'Work packages only (excludes general conditions & change orders)';

const HCChart = ({
  title,
  subtitle = DEFAULT_SUBTITLE,
  fillColor,
  borderColor,
  actualData,
  planData,
  laborType,
  onPlanPointDoubleClick,
  height = '210px',
  onExpand,
  expandAriaLabel,
}) => {
  // Filter out null/undefined y-values so the tooltip doesn't anchor to stale points
  const filteredActual = (actualData || []).filter((d) => d.y != null);
  const xAxisWeekInterval = getXAxisWeekInterval(planData, actualData);

  const handlePointDoubleClick = (args) => {
    if (!onPlanPointDoubleClick) return;

    const seriesName = args?.series?.name;
    if (seriesName !== 'Planned' && seriesName !== 'Actual') return;

    const rawDate = args.point?.x || args.point?.xValue || args.data?.pointX;
    const pointDate = new Date(rawDate);
    if (isNaN(pointDate.getTime())) return;

    onPlanPointDoubleClick({
      laborType,
      // Points are local-noon calendar dates — read the local day, not UTC
      weekEnding: parseCalendarYmd(pointDate),
      plannedFTE: seriesName === 'Planned' ? args.point?.y : undefined,
    });
  };

  return (
    <Card>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 1,
          mb: 1.5,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <SecLabel sx={{ mb: 0 }}>{title}</SecLabel>
          {subtitle && (
            <Typography
              sx={{
                mt: 0.25,
                fontSize: '9px',
                fontWeight: 400,
                letterSpacing: 0,
                textTransform: 'none',
                color: 'text.secondary',
                lineHeight: 1.3,
              }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>
        {onExpand && (
          <IconButton
            size="small"
            onClick={(event) => {
              event.stopPropagation();
              onExpand();
            }}
            title="Expand chart"
            aria-label={expandAriaLabel || `Expand ${title}`}
            sx={{ mt: -0.75, mr: -0.75 }}
          >
            <FullscreenIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      {/* Custom legend (Syncfusion legend hidden) */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: '2px',
              bgcolor: fillColor,
              opacity: 0.45,
              border: `0.5px solid ${borderColor}`,
            }}
          />
          <Typography sx={{ fontSize: '11px', color: 'text.secondary' }}>
            Actual FTE (bars)
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 16, height: 2, bgcolor: '#534AB7' }} />
          <Typography sx={{ fontSize: '11px', color: 'text.secondary' }}>
            Planned FTE
          </Typography>
        </Box>
      </Box>

      <ChartComponent
        height={height}
        primaryXAxis={{
          valueType: 'DateTime',
          labelFormat: 'MMM d',
          intervalType: 'Weeks',
          interval: xAxisWeekInterval,
          rangePadding: 'Round',
          edgeLabelPlacement: 'Shift',
          majorGridLines: { width: 0 },
          labelStyle: { size: '9px' },
        }}
        primaryYAxis={{
          majorGridLines: { color: 'rgba(136,135,128,.12)' },
          labelStyle: { size: '9px' },
          title: 'FTE',
          titleStyle: { size: '10px' },
        }}
        axisLabelRender={(args) => {
          if (args.axis.name === 'primaryXAxis') {
            const d = new Date(args.value);
            if (d.getMonth() === 0) {
              args.text = `Jan '${String(d.getFullYear()).slice(-2)}`;
            }
          }
        }}
        sharedTooltipRender={(args) => {
          // In shared mode Syncfusion fires sharedTooltipRender (NOT
          // tooltipRender), and the args are arrays aligned per visible series.
          const seriesArr = args?.series || [];
          const pointArr = args?.point || [];

          // Format the date header (derive from the point x to avoid
          // re-parsing an already-formatted header string).
          const headerX = pointArr[0]?.x;
          if (headerX != null) {
            const d = new Date(headerX);
            if (!isNaN(d)) {
              args.headerText = d.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
              });
            }
          }

          args.text = seriesArr.map((series, i) => {
            const point = pointArr[i];
            const name = series?.name;

            if (name === 'Actual') {
              const row = series?.dataSource?.[point?.index] || {};
              const fte =
                typeof point?.y === 'number' ? point.y.toFixed(1) : '0.0';
              const headcount = row.workers == null ? '0' : String(row.workers);
              const multiplier =
                row.overtimeMultiplier == null
                  ? '—'
                  : `${Number(row.overtimeMultiplier).toFixed(2)}x`;
              const avgHours =
                row.avgHours == null
                  ? '—'
                  : `${Number(row.avgHours).toFixed(0)} hrs avg`;
              return `Actual: <b>${fte} FTE</b><br/>Headcount: <b>${headcount}</b><br/>${multiplier} (${avgHours})`;
            }

            if (name === 'Planned') {
              const y = point?.y;
              return `Planned: <b>${typeof y === 'number' ? y.toFixed(1) : y} FTE</b>`;
            }

            return args.text?.[i] ?? '';
          });
        }}
        tooltip={{
          enable: true,
          shared: true,
          fadeOutDuration: 200,
        }}
        pointDoubleClick={handlePointDoubleClick}
        legendSettings={{ visible: false }}
        chartArea={{ border: { width: 0 } }}
      >
        <Inject services={[ColumnSeries, LineSeries, DateTime, ChartTooltip]} />
        <SeriesCollectionDirective>
          {/* Planned series FIRST — it spans the full date range,
              so the shared tooltip header derives its date from this series */}
          <SeriesDirective
            dataSource={planData}
            xName="x"
            yName="y"
            name="Planned"
            type="Line"
            fill="#534AB7"
            width={2.5}
          />
          <SeriesDirective
            dataSource={filteredActual}
            xName="x"
            yName="y"
            name="Actual"
            type="Column"
            fill={fillColor}
            opacity={0.4}
            border={{ color: borderColor, width: 0.5 }}
          />
        </SeriesCollectionDirective>
      </ChartComponent>
      <Typography
        sx={{
          mt: 1,
          fontSize: '9px',
          fontWeight: 400,
          color: 'text.secondary',
          lineHeight: 1.35,
          textAlign: 'left',
        }}
      >
        <Tooltip
          title="It measures crew effort, not headcount — e.g. 130 workers on overtime can equal 151.8 FTE. Using FTE lets actual crew load be compared directly against the planned budget."
          arrow
          placement="top-start"
          slotProps={{
            tooltip: {
              sx: { fontSize: '11px', maxWidth: 320, lineHeight: 1.4 },
            },
          }}
        >
          <Box
            component="span"
            tabIndex={0}
            sx={{
              borderBottom: '1px dotted currentColor',
              cursor: 'help',
            }}
          >
            FTE (Full-Time Equivalent)
          </Box>
        </Tooltip>
        {' = total labor hours ÷ 40.'}
      </Typography>
    </Card>
  );
};

export default HCChart;
